(() => {
  class SideCart {
    constructor(element) {
      this.el = element;
      this.panel = this.el.querySelector('.side-cart__panel');
      this.itemsEl = this.el.querySelector('[data-side-cart-items]');
      this.emptyEl = this.el.querySelector('[data-side-cart-empty]');
      this.bogoEl = this.el.querySelector('[data-side-cart-bogo-notice]');
      this.subtotalEl = this.el.querySelector('[data-side-cart-subtotal]');
      this.rushEl = this.el.querySelector('[data-side-cart-rush]');
      this.closeButtons = this.el.querySelectorAll('[data-side-cart-close]');
      this.triggerSelector = '[data-side-cart-trigger]';
      this.moneyFormat = this.el.dataset.moneyFormat || '${{amount}}';
      this.rushEnabled = this.el.dataset.rushEnabled === 'true';
      this.rushVariantId = this.el.dataset.rushVariantId || '';
      this.rushLabel = this.el.dataset.rushLabel || 'Rush Processing';
      this.rushPrice = this.el.dataset.rushPrice || '$200.00';
      this.isOpen = false;
      this.lastItemCount = null;
      this.activeTrigger = null;
      this.refreshRequestId = 0;
      this.bundleComponentsCache = new Map();

      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.handleCartCountUpdate = this.handleCartCountUpdate.bind(this);
      this.lastCartCountUpdateAt = 0;

      this.syncRushConfigFromPage();
      this.bindEvents();
      this.observeExternalCartAdds();
    }

    syncRushConfigFromPage() {
      const rushBlock = document.querySelector('[data-variant-id][class*="ai-rush-processing-"]');

      if (!rushBlock) {
        return;
      }

      const variantId = rushBlock.dataset.variantId || '';
      const labelTextEl = rushBlock.querySelector('label span:not([class*="icon"]):not([class*="price"])');

      if (variantId) {
        this.rushEnabled = true;
        this.rushVariantId = variantId;
      }

      if (labelTextEl && labelTextEl.textContent.trim()) {
        this.rushLabel = labelTextEl.textContent.trim();
      }
    }

    bindEvents() {
      const maxItemQuantity = 5000;
      document.body.classList.add('side-cart-enabled');

      document.addEventListener('click', (event) => {
        const trigger = event.target.closest(this.triggerSelector);

        if (trigger) {
          event.preventDefault();
          this.activeTrigger = trigger;
          this.open();
          return;
        }

        const quantityButton = event.target.closest('[data-side-cart-quantity]');

        if (quantityButton) {
          event.preventDefault();
          this.updateItemQuantity(
            quantityButton.dataset.key,
            Number(quantityButton.dataset.quantity)
          );
          return;
        }

        const removeButton = event.target.closest('[data-side-cart-remove]');

        if (removeButton) {
          event.preventDefault();
          this.updateItemQuantity(removeButton.dataset.key, 0);
          return;
        }

      });

      document.addEventListener('change', (event) => {
        const quantityInput = event.target.closest('[data-side-cart-quantity-input]');

        if (quantityInput) {
          const quantity = Math.min(Math.floor(Number(quantityInput.value)), maxItemQuantity);
          const currentQuantity = Number(quantityInput.dataset.currentQuantity);

          if (!Number.isFinite(quantity) || quantity < 1) {
            quantityInput.value = currentQuantity;
            return;
          }

          quantityInput.value = quantity;

          if (quantity !== currentQuantity) {
            this.updateItemQuantity(quantityInput.dataset.key, quantity);
          }
          return;
        }

        const rushToggle = event.target.closest('[data-side-cart-rush-toggle]');

        if (!rushToggle) {
          return;
        }

        this.toggleRushProcessing(rushToggle.checked);
      });

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      document.addEventListener('keydown', this.handleDocumentKeydown);
      document.addEventListener('keydown', (event) => {
        const quantityInput = event.target.closest('[data-side-cart-quantity-input]');

        if (quantityInput && event.key === 'Enter') {
          event.preventDefault();
          quantityInput.blur();
        }
      });
      window.addEventListener('cartcount:update', this.handleCartCountUpdate);
    }

    handleDocumentKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) {
        this.close();
      }
    }

    handleCartCountUpdate(event) {
      this.lastCartCountUpdateAt = Date.now();
      const cart = event.detail && Array.isArray(event.detail.items) ? event.detail : null;
      const nextCount = cart && typeof cart.item_count === 'number'
        ? cart.item_count
        : event.detail && typeof event.detail.item_count === 'number'
        ? event.detail.item_count
        : null;

      if (nextCount !== null) {
        if (!document.body.classList.contains('template-cart')) {
          this.open(false);
        }

        this.lastItemCount = nextCount;
      }

      // Always fetch the authoritative cart. Shopify can emit an intermediate
      // add-to-cart response while Buy X Get Y discounts are still being
      // allocated, which can temporarily contain split/duplicate line items.
      this.refresh();
    }

    observeExternalCartAdds() {
      // Some Shopify app/AI blocks add through cart/add.js but do not publish the
      // theme's cartcount:update event. Keep the header count and drawer in sync
      // for those blocks without duplicating the native theme flow.
      if (window.__sideCartCartAddObserverInstalled) {
        return;
      }

      window.__sideCartCartAddObserverInstalled = true;
      const nativeFetch = window.fetch.bind(window);

      window.fetch = (...args) => {
        const request = args[0];
        const requestUrl = typeof request === 'string' ? request : request && request.url;
        const responsePromise = nativeFetch(...args);

        if (!requestUrl || !/\/cart\/add\.js(?:[?#]|$)/.test(requestUrl)) {
          return responsePromise;
        }

        const updateStartedAt = this.lastCartCountUpdateAt;

        responsePromise.then((response) => {
          if (!response.ok) {
            return;
          }

          window.setTimeout(async () => {
            // The standard product forms already dispatch this event. Only fill
            // the gap when the caller did not, as with the Best Sellers block.
            if (this.lastCartCountUpdateAt > updateStartedAt) {
              return;
            }

            try {
              const cartResponse = await nativeFetch(`${window.Theme.routes.cart_url}.js`, {
                headers: { Accept: 'application/json' },
                cache: 'no-store'
              });

              if (!cartResponse.ok) {
                return;
              }

              const cart = await cartResponse.json();
              window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
            } catch (error) {
              // A failed sync must never interfere with a successful add-to-cart.
            }
          }, 300);
        }).catch(() => {
          // Preserve the original caller's error handling.
        });

        return responsePromise;
      };
    }

    open(refreshCart = true) {
      this.isOpen = true;
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');
      document.body.classList.add('side-cart-enabled', 'side-cart-open');

      if (refreshCart) {
        this.refresh();
      }
    }

    close() {
      this.isOpen = false;
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('side-cart-open');

      if (this.activeTrigger) {
        this.activeTrigger.focus();
      }
    }

    async refresh() {
      const requestId = ++this.refreshRequestId;

      try {
        const response = await fetch(`${window.Theme.routes.cart_url}.js`, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          cache: 'no-store'
        });

        if (!response.ok) {
          throw new Error('Unable to load cart');
        }

        const cart = await response.json();

        if (requestId !== this.refreshRequestId) {
          return;
        }

        this.render(cart);
      } catch (error) {
        if (requestId !== this.refreshRequestId) {
          return;
        }

        this.itemsEl.innerHTML = '<p class="side-cart__status">We could not load your cart.</p>';
      }
    }

    getCartUpdateUrl() {
      const cartUrl = window.Theme && window.Theme.routes && window.Theme.routes.cart_url
        ? window.Theme.routes.cart_url
        : '/cart';

      return `${cartUrl.replace(/\/$/, '')}/update.js`;
    }

    render(cart) {
      const visibleItems = cart.items.filter((item) => {
        return !(this.rushEnabled && this.rushVariantId && String(item.variant_id) === this.rushVariantId);
      });
      const hasShoppableItems = visibleItems.length > 0;

      if (!hasShoppableItems) {
        this.itemsEl.innerHTML = '';
        this.emptyEl.hidden = false;
      } else {
        this.emptyEl.hidden = true;
        this.itemsEl.innerHTML = visibleItems.map((item) => this.renderItem(item)).join('');
        this.renderBundleComponents(visibleItems);
      }

      this.subtotalEl.textContent = this.formatMoney(cart.total_price);
      window.dispatchEvent(new CustomEvent('cart:bundles:update', { detail: cart }));
      this.renderBogoNotice(visibleItems);
      this.renderRush(cart);
    }

    renderBogoNotice(items) {
      if (!this.bogoEl) {
        return;
      }

      const eligibleQuantity = items.reduce((total, item) => {
        const originalUnitPrice = typeof item.original_price === 'number'
          ? item.original_price
          : item.price;
        const isEligible = Number(originalUnitPrice) <= 1000;

        return isEligible ? total + item.quantity : total;
      }, 0);
      const promotionGroupSize = 10;
      const minNoticeQuantity = 2;
      const remainder = eligibleQuantity % promotionGroupSize;

      this.bogoEl.classList.remove('side-cart__bogo-notice--unlocked');

      if (eligibleQuantity >= minNoticeQuantity && remainder >= minNoticeQuantity) {
        const remainingQuantity = promotionGroupSize - remainder;
        const remainingLabel = remainingQuantity === 1 ? 'one roll' : `${remainingQuantity} rolls`;
        const eligibleRollLabel = remainingQuantity === 1 ? 'roll' : 'rolls';
        const progressHeading = eligibleQuantity < promotionGroupSize
          ? `You're ${remainingLabel} away!`
          : `You're ${remainingLabel} away from 2 more free rolls!`;

        this.bogoEl.innerHTML = `
          <span class="side-cart__bogo-icon" aria-hidden="true">+</span>
          <span class="side-cart__bogo-copy">
            <strong>${progressHeading}</strong>
            <span>Add ${remainingQuantity} more eligible ${eligibleRollLabel} priced $10 or less — the 2 lowest-priced eligible rolls will be FREE.</span>
          </span>
        `;
        this.bogoEl.hidden = false;
        return;
      }

      if (eligibleQuantity >= promotionGroupSize && remainder === 0) {
        this.bogoEl.classList.add('side-cart__bogo-notice--unlocked');
        this.bogoEl.innerHTML = `
          <span class="side-cart__bogo-icon" aria-hidden="true">✓</span>
          <span class="side-cart__bogo-copy">
            <strong>Your 2 free rolls are unlocked!</strong>
            <span>The discount is applied automatically.</span>
          </span>
        `;
        this.bogoEl.hidden = false;
        return;
      }

      this.bogoEl.hidden = true;
      this.bogoEl.innerHTML = '';
    }

    renderItem(item) {
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="side-cart__item-variant">${this.escapeHtml(item.variant_title)}</p>`
        : '';
      const image = item.image
        ? `<img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.product_title)}" loading="lazy">`
        : '';
      const minusQuantity = Math.max(item.quantity - 1, 0);

      return `
        <article class="side-cart__item">
          <a class="side-cart__item-image" href="${this.escapeHtml(item.url || window.Theme.routes.cart_url)}">
            ${image}
          </a>
          <div class="side-cart__item-content">
            <a class="side-cart__item-title" href="${this.escapeHtml(item.url || window.Theme.routes.cart_url)}">
              ${this.escapeHtml(item.product_title)}
            </a>
            ${variantTitle}
            <div class="side-cart__bundle-components" data-side-cart-bundle-components="${this.escapeHtml(item.key)}" hidden></div>
            <div class="side-cart__item-meta">
              <div class="side-cart__quantity">
                <button type="button" data-side-cart-quantity data-key="${this.escapeHtml(item.key)}" data-quantity="${minusQuantity}" aria-label="Decrease quantity">-</button>
                <input
                  type="number"
                  inputmode="numeric"
                  min="1"
                  max="5000"
                  step="1"
                  value="${item.quantity}"
                  data-side-cart-quantity-input
                  data-key="${this.escapeHtml(item.key)}"
                  data-current-quantity="${item.quantity}"
                  aria-label="Quantity for ${this.escapeHtml(item.product_title)}"
                >
                <button type="button" data-side-cart-quantity data-key="${this.escapeHtml(item.key)}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
              </div>
              <div class="side-cart__item-prices">
  <span class="side-cart__item-unit-price">
    Unit Price: ${this.formatMoney(item.final_price)} each
  </span>
  <strong class="side-cart__item-price">
    Total: ${this.formatMoney(item.final_line_price)}
  </strong>
</div>
            </div>
            <button class="side-cart__remove" type="button" data-side-cart-remove data-key="${this.escapeHtml(item.key)}">
              Remove
            </button>
          </div>
        </article>
      `;
    }

    async loadBundleComponents(variantId) {
      const cacheKey = String(variantId);

      if (this.bundleComponentsCache.has(cacheKey)) {
        return this.bundleComponentsCache.get(cacheKey);
      }

      const request = (async () => {
        const components = [];
        let after = null;

        do {
          const response = await fetch('/api/2026-07/graphql.json', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            signal: AbortSignal.timeout(10000),
            body: JSON.stringify({
              query: `query SideCartBundleContents($id: ID!, $after: String) {
                node(id: $id) {
                  ... on ProductVariant {
                    components(first: 50, after: $after) {
                      nodes {
                        quantity
                        productVariant {
                          id
                          title
                          product { title }
                        }
                      }
                      pageInfo { hasNextPage endCursor }
                    }
                  }
                }
              }`,
              variables: {
                id: `gid://shopify/ProductVariant/${variantId}`,
                after
              }
            })
          });

          if (!response.ok) {
            throw new Error('Unable to load bundle contents');
          }

          const result = await response.json();
          const connection = result.data?.node?.components;

          if (result.errors?.length || !connection) {
            throw new Error('Bundle contents unavailable');
          }

          components.push(...connection.nodes);
          after = connection.pageInfo.hasNextPage ? connection.pageInfo.endCursor : null;
        } while (after);

        return components;
      })();

      this.bundleComponentsCache.set(cacheKey, request);

      try {
        return await request;
      } catch (error) {
        this.bundleComponentsCache.delete(cacheKey);
        throw error;
      }
    }

    async renderBundleComponents(items) {
      await Promise.all(items.map(async (item) => {
        const target = this.itemsEl.querySelector(`[data-side-cart-bundle-components="${CSS.escape(item.key)}"]`);

        if (!target) {
          return;
        }

        try {
          const components = await this.loadBundleComponents(item.variant_id);

          if (!components.length || !target.isConnected) {
            return;
          }

          target.innerHTML = `
            <span class="side-cart__bundle-label">Includes:</span>
            <ul class="side-cart__bundle-list">
              ${components.map(({ quantity, productVariant }) => {
                const variantTitle = productVariant.title && productVariant.title !== 'Default Title'
                  ? productVariant.title
                  : productVariant.product.title;

                return `<li><strong>${this.escapeHtml(quantity)} ×</strong> ${this.escapeHtml(variantTitle)}</li>`;
              }).join('')}
            </ul>
          `;
          target.hidden = false;
        } catch (error) {
          // Non-bundle variants and transient API failures should not affect cart use.
          target.hidden = true;
          target.innerHTML = '';
        }
      }));
    }

    renderRush(cart) {
      const rushItem = this.rushEnabled && this.rushVariantId
        ? cart.items.find((item) => String(item.variant_id) === this.rushVariantId)
        : null;

      if (!this.rushEnabled || !this.rushVariantId) {
        this.rushEl.hidden = true;
        this.rushEl.innerHTML = '';
        return;
      }

      const checked = rushItem ? 'checked' : '';
      const price = rushItem ? this.formatMoney(rushItem.final_price) : this.rushPrice;

      this.rushEl.hidden = false;
      this.rushEl.innerHTML = `
        <label class="side-cart__rush-label">
          <input type="checkbox" data-side-cart-rush-toggle ${checked}>
          <span class="side-cart__rush-copy">
            <span class="side-cart__rush-title">${this.escapeHtml(this.rushLabel)}</span>
            <span class="side-cart__rush-text">
  Priority processing for faster internal handling. 
  <strong>
    Note: </strong> Not available for Foam Tapes or products with a 1.5-inch core. 

</span>
          <strong class="side-cart__rush-price">${this.escapeHtml(price)}</strong>
        </label>
      `;
    }

    async updateItemQuantity(key, quantity) {
      if (!key) return;

      this.panel.classList.add('is-loading');

      try {
        const response = await fetch(`${window.Theme.routes.cart_change_url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ id: key, quantity })
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || error.message || 'We could not update your cart.');
        }

        const cart = await response.json();
        window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
      } catch (error) {
        this.itemsEl.insertAdjacentHTML(
          'afterbegin',
          `<p class="side-cart__status">${this.escapeHtml(error.message || 'We could not update your cart.')}</p>`
        );
        this.refresh();
      } finally {
        this.panel.classList.remove('is-loading');
      }
    }

    async toggleRushProcessing(enabled) {
      this.panel.classList.add('is-loading');

      try {
        const response = await fetch(this.getCartUpdateUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            updates: {
              [this.rushVariantId]: enabled ? 1 : 0
            }
          })
        });

        if (!response.ok) {
          throw new Error('Unable to update rush processing');
        }

        const cart = await response.json();
        window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
      } catch (error) {
        this.rushEl.insertAdjacentHTML('beforeend', '<p class="side-cart__status">We could not update Rush Processing.</p>');
      } finally {
        this.panel.classList.remove('is-loading');
      }
    }

    formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, this.moneyFormat);
      }

      return `$${(cents / 100).toFixed(2)}`;
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  class ProductCheckoutButton {
    constructor() {
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleDocumentChange = this.handleDocumentChange.bind(this);
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('change', this.handleDocumentChange);
      this.syncAll();
    }

    syncAll() {
      document.querySelectorAll('[data-product-form]').forEach((form) => {
        this.syncButtonState(form);
      });
    }

    syncButtonState(form) {
      const checkoutButton = form.querySelector('[data-product-checkout]');
      const atcButton = form.querySelector('[data-product-atc]');

      if (!checkoutButton || !atcButton) {
        return;
      }

      const isDisabled = atcButton.disabled || !this.getVariantId(form);
      checkoutButton.disabled = isDisabled;
      checkoutButton.classList.toggle('disabled', isDisabled);
    }

    getVariantId(form) {
      const scope = form.closest('[data-product-wrapper]') || document;
      const variantInputs = [
        form.querySelector('[data-variants]'),
        ...scope.querySelectorAll('[data-product-form] [data-variants]')
      ].filter(Boolean);

      for (const variantInput of variantInputs) {
        const variantId = variantInput.value;

        if (variantId && variantId !== 'not-selected') {
          return variantId;
        }
      }

      return '';
    }

    getQuantity(form) {
      const quantityInput = form.querySelector('[name="quantity"]');
      const quantity = Number(quantityInput && quantityInput.value ? quantityInput.value : 1);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    getProperties(form) {
      const scope = form.closest('[data-product-wrapper]') || document;
      const fields = [
        ...form.querySelectorAll('[name^="properties["]'),
        ...scope.querySelectorAll('[data-product-form] [name^="properties["]')
      ].filter(Boolean);
      const properties = {};

      fields.forEach((field) => {
        if (field.disabled || !field.name) {
          return;
        }

        if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) {
          return;
        }

        const match = field.name.match(/^properties\[(.+)\]$/);

        if (!match || field.value === '') {
          return;
        }

        properties[match[1]] = field.value;
      });

      return properties;
    }

    setProcessing(button, isProcessing) {
      button.disabled = isProcessing;
      button.classList.toggle('processing', isProcessing);
    }

    async handleDocumentClick(event) {
      const checkoutButton = event.target.closest('[data-product-checkout]');

      if (!checkoutButton) {
        return;
      }

      event.preventDefault();

      const form = checkoutButton.closest('[data-product-form]');

      if (!form || checkoutButton.disabled) {
        return;
      }

      const variantId = this.getVariantId(form);

      if (!variantId) {
        this.syncButtonState(form);
        return;
      }

      this.setProcessing(checkoutButton, true);

      try {
        const lineItem = {
          id: Number(variantId),
          quantity: this.getQuantity(form)
        };
        const properties = this.getProperties(form);

        if (Object.keys(properties).length) {
          lineItem.properties = properties;
        }

        const response = await fetch(`${window.Theme.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [lineItem]
          })
        });

        if (!response.ok) {
          throw new Error('Unable to add item to cart');
        }

        window.location.href = `${window.Theme.routes.root_url}checkout`;
      } catch (error) {
        this.setProcessing(checkoutButton, false);
        this.syncButtonState(form);
      }
    }

    handleDocumentChange(event) {
      const form = event.target.closest('[data-product-form]');

      if (!form) {
        return;
      }

      window.requestAnimationFrame(() => this.syncButtonState(form));
    }
  }

  const init = () => {
    const sideCart = document.querySelector('[data-side-cart]');

    if (sideCart) {
      new SideCart(sideCart);
    }

    if (document.querySelector('[data-product-checkout]')) {
      new ProductCheckoutButton();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
    
